import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ROLE_SCALE,
  fitTextToSlot,
  normalizeRole,
  screenStyleForRole,
  selectableColorsForRole,
  getRoleLabel,
  getTextColorLabel
} from './typography';
import './AtelierTextEditor.css';

// Edition du texte DIRECTEMENT sur la page (cahier des charges typographique
// §1). Le champ se superpose exactement a l'emplacement et adopte la
// typographie reelle du role, mise a l'echelle de la page affichee : ce que
// l'utilisateur voit en tapant est ce que le PDF imprimera (§18).
//
// Volontairement PAS un editeur riche (§2) : pas de police libre, pas
// d'effets, pas de rotation, pas de positionnement libre.
//
// CORRECTIF 8 (2026-10-16) — « la pop-up de texte est compliquee » : on ecrit dans la page, a la VRAIE taille
// (plus d'agrandissement ni de note « affiche en plus grand » : la page est fidele a l'impression), avec une
// barre flottante compacte AU-DESSUS du texte, quatre reglages :
//   1. Style  : Titre · Citation · Légende (trois puces ; une puce active qu'on re-clique redonne le texte
//               courant). « Sous-titre » et « Texte courant » restent derriere « ⋯ ».
//   2. Taille : A− / A+ par paliers (jamais de curseur ni de « pt »).
//   3. Alignement : UN bouton qui alterne gauche / centre / droite.
//   4. Couleur : une pastille qui ouvre les choix de la palette fermee.
// Plus de bouton « Enregistrer » : l'enregistrement est automatique — on termine par un clic ailleurs, Echap
// ou Ctrl/Cmd+Entree. Il n'y a toujours aucun selecteur de police (§4).

// Pas d'une pression sur A− / A+ (en points), par role.
const SIZE_STEP_PT = { title: 2, subtitle: 1, body: 1, caption: 1, quote: 2 };
// Puces de style visibles ; les autres roles sont dans le menu « ⋯ ».
const CHIP_ROLES = ['title', 'quote', 'caption'];
const MORE_ROLES = ['body', 'subtitle'];
const ALIGN_CYCLE = ['left', 'center', 'right'];

function AlignIcon({ mode }) {
  // Largeurs de lignes evoquant chaque alignement — plus lisible qu'un
  // libelle texte dans une barre aussi petite.
  const rows = [100, 60, 90, 50];
  return (
    <svg width="16" height="16" viewBox="0 0 14 14" aria-hidden="true">
      {rows.map((width, index) => {
        const w = (width / 100) * 12;
        const x = mode === 'right' ? 13 - w : mode === 'center' ? (14 - w) / 2 : 1;
        return <rect key={index} x={x} y={2 + index * 3} width={w} height="1.4" rx="0.7" fill="currentColor" />;
      })}
    </svg>
  );
}

function AtelierTextEditor({
  item,
  rect,
  role,
  styleOverrides,
  printFormat,
  // Largeur de l'incrustation, en px ET en mm : les deux decrivent la meme
  // chose (la zone de contenu de la page), ce qui donne l'echelle exacte.
  reference,
  slotWidthMm,
  slotHeightMm,
  onSave,
  // Page de texte seul (correctif 7) : limite de caracteres affichee en compteur, texte centre verticalement
  // pendant la saisie comme a l'impression, et « Continuer sur la page suivante » (recoit le surplus colle).
  maxChars = null,
  verticalCenter = false,
  onContinueNextPage = null
}) {
  const { t } = useTranslation('atelier');
  const safeRole = normalizeRole(role);
  const [draft, setDraft] = useState(item?.text || '');
  const [currentRole, setCurrentRole] = useState(safeRole);
  const [overrides, setOverrides] = useState(styleOverrides || {});
  const [openMenu, setOpenMenu] = useState(null); // 'color' | 'more' | null
  const [barPosition, setBarPosition] = useState(null);
  const [limitHelpOpen, setLimitHelpOpen] = useState(false);
  // Surplus d'un texte COLLE qui depassait la limite : propose a la page suivante plutot que perdu.
  const [carry, setCarry] = useState('');
  const fieldRef = useRef(null);
  const rootRef = useRef(null);
  const barRef = useRef(null);
  const finishedRef = useRef(false);

  // Le curseur doit etre dans le texte des l'ouverture : l'utilisateur a
  // clique POUR ecrire, lui demander un second clic serait absurde.
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
  }, []);

  // « Terminer » = enregistrer. Un seul chemin (jamais deux enregistrements pour une meme fermeture).
  const latest = useRef({});
  latest.current = { text: draft, role: currentRole, styleOverrides: overrides };
  const finish = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onSave(latest.current);
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  // Echap et Ctrl/Cmd+Entree terminent ; un clic en dehors (champ ET barre) termine aussi : perdre ce que
  // l'on vient de taper parce qu'on a clique a cote est le pire comportement possible pour un editeur en ligne.
  useEffect(() => {
    function handleKey(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        finishRef.current();
      } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        finishRef.current();
      }
    }
    function handleOutside(event) {
      const inside = (rootRef.current && rootRef.current.contains(event.target))
        || (barRef.current && barRef.current.contains(event.target));
      if (!inside) finishRef.current();
    }
    document.addEventListener('keydown', handleKey);
    // `true` : capture, pour passer avant les gestionnaires de clic des
    // emplacements voisins (qui ouvriraient aussitot un autre editeur).
    document.addEventListener('mousedown', handleOutside, true);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleOutside, true);
    };
  }, []);

  // Adaptation automatique en temps reel (§9) : calculee avec EXACTEMENT la
  // meme fonction que le serveur, donc l'alerte affichee ici correspond a ce
  // que le controle avant commande dira.
  const fit = fitTextToSlot({
    text: draft, role: currentRole, formatId: printFormat, slotWidthMm, slotHeightMm, overrides
  });
  const scale = ROLE_SCALE[currentRole] || ROLE_SCALE.body;
  // La taille CHOISIE (A− / A+) fait foi, comme au rendu serveur ; sinon l'ajustement automatique.
  const hasExplicitSize = Number.isFinite(Number(overrides.sizePt));
  const shownPt = hasExplicitSize
    ? Math.min(scale.maxPt, Math.max(scale.floorPt != null ? scale.floorPt : scale.minPt, Number(overrides.sizePt)))
    : fit.fontSizePt;
  const shownAlign = overrides.align || fit.align;

  const trueStyle = screenStyleForRole(currentRole, printFormat, reference, {
    ...overrides,
    sizePt: shownPt,
    align: shownAlign
  });

  // Remplissage de l'emplacement : sert aux alertes (jamais affiche en pourcentage).
  const heightRatio = fit.usableHeightMm > 0 ? fit.estimatedHeightMm / fit.usableHeightMm : 0;
  const lineRatio = fit.maxLines ? fit.lines / fit.maxLines : 0;
  const fillPct = Math.round(Math.max(heightRatio, lineRatio) * 100);
  const isAtLimit = fit.status === 'overflow';
  const NEAR_LIMIT_PCT = 85;
  const isNearLimit = !isAtLimit && fillPct >= NEAR_LIMIT_PCT;
  const atSmallestSize = fit.fontSizePt <= fit.minPt;

  // A la vraie taille de l'impression : pas d'agrandissement du champ.
  // Texte centre VERTICALEMENT (page de texte seul) : un champ de texte ne sait pas centrer son contenu, on
  // calcule donc la marge du haut a partir de la hauteur reelle du texte — comme sur la page imprimee.
  const [centerPad, setCenterPad] = useState(0);
  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!verticalCenter || !field) { setCenterPad(0); return; }
    // scrollHeight ne descend jamais sous la hauteur du champ : on le ramene a 0 le temps de mesurer la hauteur
    // REELLE du texte, puis on rend la main.
    const previousPad = field.style.paddingTop;
    const previousHeight = field.style.height;
    const boxHeight = field.clientHeight;
    field.style.paddingTop = '0px';
    field.style.height = '0px';
    const contentHeight = field.scrollHeight;
    field.style.height = previousHeight;
    field.style.paddingTop = previousPad;
    const free = boxHeight - contentHeight;
    setCenterPad(free > 0 ? Math.round(free / 2) : 0);
  }, [draft, verticalCenter, trueStyle.fontSize, trueStyle.lineHeight, rect.height]); // eslint-disable-line react-hooks/exhaustive-deps
  const fieldStyle = {
    ...trueStyle,
    overflowY: 'auto',
    // Sans fond gris : le texte est directement sur le papier (meme teinte que la page, donc le texte deja
    // imprime dessous ne transparait pas) ; le cadre n'apparait qu'en edition.
    background: '#ffffff',
    boxShadow: 'none',
    ...(verticalCenter && centerPad > 0 ? { paddingTop: `${centerPad}px` } : {})
  };

  // Compteur « n / 600 caracteres » (orange des 90 %).
  const charCount = draft.length;
  const limitReached = maxChars != null && (charCount >= maxChars || Boolean(carry));
  const limitNear = maxChars != null && charCount >= maxChars * 0.9;

  const handleChange = (event) => {
    const value = event.target.value;
    if (maxChars == null || value.length <= maxChars) { setDraft(value); if (carry) setCarry(''); return; }
    // Depasse : on garde ce qui rentre (coupe sur une fin de phrase ou un mot si possible).
    const inputType = event.nativeEvent && event.nativeEvent.inputType;
    const head = value.slice(0, maxChars);
    const cut = Math.max(head.lastIndexOf('. '), head.lastIndexOf('\n'), head.lastIndexOf(' '));
    const keep = inputType === 'insertFromPaste' && cut > maxChars * 0.6 ? head.slice(0, cut + 1) : head;
    setDraft(keep);
    // Le surplus d'un COLLAGE n'est pas perdu : il passera a la page suivante. Une frappe, elle, est simplement bloquee.
    setCarry(inputType === 'insertFromPaste' ? value.slice(keep.length).trim() : '');
  };
  const continueOnNextPage = () => {
    finish();
    if (onContinueNextPage) onContinueNextPage(carry);
  };

  const colors = selectableColorsForRole(currentRole);
  const currentColorToken = overrides.color || fit.colorToken;
  const currentColor = colors.find((color) => color.token === currentColorToken) || colors[0];

  // --- Reglages ------------------------------------------------------------------------------------------
  const chooseRole = (nextRole) => {
    // Une autre famille de style a d'autres bornes de taille : on repart de sa taille naturelle.
    setCurrentRole(nextRole);
    setOverrides((previous) => {
      const next = { ...previous };
      delete next.sizePt;
      return next;
    });
    setOpenMenu(null);
  };
  const step = SIZE_STEP_PT[currentRole] || 1;
  const floorPt = scale.floorPt != null ? scale.floorPt : scale.minPt;
  const canShrink = shownPt - step >= floorPt - 0.001;
  const canGrow = shownPt + step <= scale.maxPt + 0.001;
  const changeSize = (direction) => {
    const next = Math.min(scale.maxPt, Math.max(floorPt, shownPt + direction * step));
    setOverrides((previous) => ({ ...previous, sizePt: next }));
  };
  const cycleAlign = () => {
    const index = ALIGN_CYCLE.indexOf(shownAlign);
    const next = ALIGN_CYCLE[(index + 1) % ALIGN_CYCLE.length]; // « justifie » (texte courant) repart a gauche
    setOverrides((previous) => ({ ...previous, align: next }));
  };

  // --- Position de la barre : AU-DESSUS du texte ---------------------------------------------------------
  // Posee dans <body> en position fixe (la page rogne tout ce qui deborde), recalee a chaque image sur le
  // champ ; au-dessus s'il y a la place, sinon juste en dessous.
  useLayoutEffect(() => {
    let frame = 0;
    const tick = () => {
      const anchor = rootRef.current;
      if (anchor) {
        const box = anchor.getBoundingClientRect();
        const bar = barRef.current ? barRef.current.getBoundingClientRect() : { width: 0, height: 40 };
        const left = Math.max(8, Math.min(window.innerWidth - bar.width - 8, box.left + box.width / 2 - bar.width / 2));
        let top = box.top - bar.height - 8;
        if (top < 8) top = box.bottom + 8 + bar.height < window.innerHeight ? box.bottom + 8 : Math.max(8, box.top + 8);
        const next = { left: Math.round(left), top: Math.round(top) };
        setBarPosition((previous) => (previous && previous.left === next.left && previous.top === next.top ? previous : next));
      }
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, []);

  // Garder le curseur dans le texte quand on clique un reglage.
  const keepFocus = (event) => event.preventDefault();

  const bar = (
    <div
      ref={barRef}
      className="atelier-text-bar"
      role="toolbar"
      aria-label={t('textEditor.toolbarLabel')}
      style={{ left: barPosition ? barPosition.left : -9999, top: barPosition ? barPosition.top : -9999 }}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="atelier-text-bar-row">
        <div className="atelier-text-bar-chips" role="group" aria-label={t('textEditor.styleLabel')}>
          {CHIP_ROLES.map((value) => (
            <button
              key={value}
              type="button"
              className={`atelier-text-chip ${currentRole === value ? 'is-active' : ''}`}
              aria-pressed={currentRole === value}
              onMouseDown={keepFocus}
              onClick={() => chooseRole(currentRole === value ? 'body' : value)}
            >
              {getRoleLabel(value, t)}
            </button>
          ))}
        </div>
        <span className="atelier-text-bar-sep" aria-hidden="true" />
        <button
          type="button"
          className="atelier-text-bar-btn"
          onMouseDown={keepFocus}
          onClick={() => changeSize(-1)}
          disabled={!canShrink}
          aria-label={t('textEditor.smaller')}
        >
          A<small>−</small>
        </button>
        <button
          type="button"
          className="atelier-text-bar-btn"
          onMouseDown={keepFocus}
          onClick={() => changeSize(1)}
          disabled={!canGrow}
          aria-label={t('textEditor.larger')}
        >
          A<small>+</small>
        </button>
        <span className="atelier-text-bar-sep" aria-hidden="true" />
        <button
          type="button"
          className="atelier-text-bar-btn"
          onMouseDown={keepFocus}
          onClick={cycleAlign}
          aria-label={t('textEditor.alignLabel', { mode: t(`textEditor.alignModes.${shownAlign}`) })}
        >
          <AlignIcon mode={shownAlign} />
        </button>
        <span className="atelier-text-bar-menu-wrap">
          <button
            type="button"
            className="atelier-text-swatch is-trigger"
            style={{ background: currentColor ? currentColor.hex : '#241f18' }}
            onMouseDown={keepFocus}
            onClick={() => setOpenMenu((open) => (open === 'color' ? null : 'color'))}
            aria-haspopup="menu"
            aria-expanded={openMenu === 'color'}
            aria-label={t('textEditor.colorGroupLabel')}
          />
          {openMenu === 'color' && (
            <span className="atelier-text-bar-menu is-colors" role="menu">
              {colors.map((color) => (
                <button
                  key={color.token}
                  type="button"
                  role="menuitem"
                  className={`atelier-text-swatch ${currentColorToken === color.token ? 'is-active' : ''}`}
                  style={{ background: color.hex }}
                  onMouseDown={keepFocus}
                  onClick={() => { setOverrides((previous) => ({ ...previous, color: color.token })); setOpenMenu(null); }}
                  aria-label={getTextColorLabel(color.token, t)}
                />
              ))}
            </span>
          )}
        </span>
        <span className="atelier-text-bar-menu-wrap">
          <button
            type="button"
            className="atelier-text-bar-btn"
            onMouseDown={keepFocus}
            onClick={() => setOpenMenu((open) => (open === 'more' ? null : 'more'))}
            aria-haspopup="menu"
            aria-expanded={openMenu === 'more'}
            aria-label={t('textEditor.more')}
          >
            ⋯
          </button>
          {openMenu === 'more' && (
            <span className="atelier-text-bar-menu" role="menu">
              {MORE_ROLES.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="menuitem"
                  className={currentRole === value ? 'is-active' : ''}
                  onMouseDown={keepFocus}
                  onClick={() => chooseRole(value)}
                >
                  {getRoleLabel(value, t)}
                </button>
              ))}
            </span>
          )}
        </span>
      </div>

      {/* Alerte LEGERE (§9) : on previent, on ne bloque pas, et on ne coupe surtout pas le texte en silence. */}
      {isAtLimit && (
        <p className="atelier-text-alert is-error">
          <strong>{t('textEditor.limitReachedTitle')}</strong>{' '}
          {atSmallestSize ? t('textEditor.atSmallestSize') : t('textEditor.doesNotFit')}{' '}
          {t('textEditor.shortenOrChangeLayout')}
        </p>
      )}
      {maxChars != null && (
        <div className={`atelier-text-counter ${limitNear ? 'is-near' : ''}`}>
          <span>{t('textEditor.counter', { count: charCount, max: maxChars })}</span>
          <button type="button" className="atelier-text-counter-info" onMouseDown={keepFocus} onClick={() => setLimitHelpOpen((open) => !open)} aria-expanded={limitHelpOpen} aria-label={t('textEditor.counterWhy')}>i</button>
        </div>
      )}
      {limitHelpOpen && <p className="atelier-text-alert is-info" role="note">{t('textEditor.counterHelp')}</p>}
      {limitReached && (
        <p className="atelier-text-alert is-warning">
          {t('textEditor.limitText')}
          {onContinueNextPage && (
            <>
              {' '}
              <button type="button" className="atelier-text-continue" onMouseDown={keepFocus} onClick={continueOnNextPage}>
                {t('textEditor.continueNextPage')}
              </button>
            </>
          )}
        </p>
      )}
      {isNearLimit && !limitReached && (
        <p className="atelier-text-alert is-warning">{t('textEditor.nearLimit', { percent: fillPct })}</p>
      )}
      {!isAtLimit && fit.status === 'reduced' && !hasExplicitSize && (
        <p className="atelier-text-alert">{t('textEditor.reducedAuto')}</p>
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className="atelier-text-editor"
      style={{ top: `${rect.top}%`, left: `${rect.left}%`, width: `${rect.width}%`, height: `${rect.height}%` }}
      onClick={(event) => event.stopPropagation()}
    >
      <textarea
        ref={fieldRef}
        className={`atelier-text-editor-field ${isAtLimit ? 'is-at-limit' : ''} ${isNearLimit ? 'is-near-limit' : ''}`}
        style={fieldStyle}
        value={draft}
        onChange={handleChange}
        aria-label={t('textEditor.editFieldLabel', { role: getRoleLabel(currentRole, t) })}
      />
      {typeof document === 'undefined' ? null : createPortal(bar, document.body)}
    </div>
  );
}

export default AtelierTextEditor;
